import {useMutation, useQueryClient} from '@github-ui/react-query'
import {createEstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import type {
  DirectTurnTelemetrySeed,
  EstablishedAhpSession,
  FirstTurnHandoff,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import {createCustomAgentPromptPreparer} from '@github-ui/ahp-session-client/custom-agent-selection'
import {ensureSessionMode} from '@github-ui/ahp-session-client/session-mode'
import {fetchTask} from './use-task'
import {readTasks, writeTasks, type TaskListPage} from './repo-task-utils'
import type {HydratedAPITask} from '../types/api-task'
import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {isReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import type {OptimisticTask} from '../types/task'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import {resolveSessionScope} from '@github-ui/ahp-relay/session-scope-prompt'
import {
  discardRelayInstructionRulesWhenUnused,
  getRelayEntry,
  reportRelayError,
} from '@github-ui/ahp-relay/relay-registry'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isCloudSessionStoreEnabled} from '@github-ui/copilot-shared-ui/utils/cloud-session-store-flag'
import {mcFetcher} from '../utils/mc-fetcher'
import {repoCloneUrl} from '../utils/repo-clone-url'
import {sortTasksByDate, transformAPITaskToUnhydrated} from '../utils/task-helpers'
import {uuid} from '../utils/uuid'
import {createMissionControlEstablishedAhpBootstrap} from '../ahp/established-ahp-bootstrap'
import {createAhpRelayDiagnostics} from '../ahp/ahp-debug-log'
import {createCopilotChatTurnLatencyTracker} from '../ahp/turn-latency'
import {beginTaskStartup, completeTaskStartup, failTaskStartup} from '../ahp/task-startup-store'
import {bindUserStartedTurn, markUserStartedTurn, unmarkUserStartedTurn} from '../utils/artifacts/user-started-turns'
import {createEnvironmentBoundTask, type CreateEnvironmentBoundTaskInput} from '../ahp/environment-task'
import {SANDBOX_ENVIRONMENT_ID} from '@github-ui/one-copilot-composer/environment-strip/environment-option'
import {
  createEnvironmentTaskLaunch,
  replaceEnvironmentTaskLaunch,
  resolveEnvironmentTaskTelemetryContext,
  storeEnvironmentTaskLaunch,
} from '../ahp/environment-task-launch-journal'
import {
  abandonInlineEnvironmentTaskLaunchOwner,
  captureEnvironmentTaskLaunchTaskResolver,
  expectInlineEnvironmentTaskLaunchOwner,
  registerInlineEnvironmentTaskLaunchOwner,
} from './use-environment-task-launch'
import {
  applyEnvironmentTaskStartupMode,
  resolveEnvironmentTaskStartupMode,
} from '../ahp/environment-task-startup-configuration'
import {
  discardInstructionRuleSession,
  InstructionRuleSessionPublisher,
} from '@github-ui/copilot-chat/utils/instruction-rules-publisher'
import {useOptionalContributedInstructionSnapshot as useLegacyContributedInstructionSnapshot} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {
  useHasContributedInstructionsProvider,
  useOptionalContributedInstructionSnapshot,
} from '@github-ui/copilot-shared-ui/contexts/ContributedInstructionsContext'
import {
  deriveInstructionRules,
  mergeContributedInstructionsMetadata,
  normalizeContributedInstructions,
  toContributedInstructionsMetadata,
} from '@github-ui/copilot-chat/utils/instruction-rules'
import type {CopilotCustomInstructions} from '@github-ui/copilot-chat-types'

export interface CreateEnvironmentTaskInput {
  environmentId: string
  prompt: PreparedPrompt
  /** Repository custom agent to resolve from the established host's AHP customization list. */
  customAgent?: {slug: string; displayName?: string}
  repositoryInstructions?: CopilotCustomInstructions[]
  /** Repository (name-with-owner) to associate + provision as a host project. */
  repositoryNwo?: string
  /** Server-provided HTTPS clone URL; host-correct, preferred over the origin-derived {@link repoCloneUrl}. */
  repositoryCloneUrl?: string
  /** Base branch for the worktree; the host auto-generates the working branch. */
  baseRef?: string
  /** Managed sandbox VM with managed Playwright MCP and sandbox startup tracking. */
  managedSandbox?: boolean
  /** Whether this environment task is being created for a Chat launch. */
  chatLaunch?: boolean
  /** Exact CLI mode selected by the unified composer. */
  agentMode?: AgentMode
  approvalMode?: SessionApprovalMode
  /**
   * Optional MCP permission mode captured by the composer. When set to a
   * non-default value, the equivalent chat runtime mode is sent as
   * `createSession.config.mode` so the opening turn runs in it.
   */
  mcpPermissionMode?: McpPermissionMode
  /** Host-specific first-turn configuration, used by established Chat surfaces. */
  configureSession?: FirstTurnHandoff['configureSession']
  /** Login used to scope repository-less managed sandbox compute. */
  scopeLogin?: string
  /** Optional Copilot Space attached while provisioning a new Cloud session. */
  space?: {owner: string; slug: string}
  clientGeneratedId?: string
  /** Accepted-Send timing for an explicitly instrumented managed-sandbox source. */
  directTurnTelemetrySeed?: DirectTurnTelemetrySeed
}

export interface CreateEnvironmentTaskResult {
  task: HydratedAPITask
  sessionId: string
  environmentId: string
}

/**
 * Cache integration that mirrors the cloud `useCreateAPITask` shape so each
 * page can pass the same optimistic config it already builds for cloud task
 * creation. When omitted, the hook still works — it just doesn't touch any
 * task list cache.
 */
export interface OptimisticTaskListConfig {
  /** Query key whose data this hook should optimistically modify. */
  queryKey: readonly unknown[]
  /** Additional query keys to invalidate (not optimistically update) after a
   * successful creation — e.g. the grouped-tasks sidebar cache. */
  additionalInvalidateKeys?: ReadonlyArray<readonly unknown[]>
  /** Maximum number of tasks to keep in the list after the optimistic
   * placeholder is replaced; matches the page's pagination size so the list
   * doesn't overflow. */
  pageSize: number
}

export interface UseCreateEnvironmentTaskOptions {
  optimisticTaskListConfig?: OptimisticTaskListConfig
}

interface MutationContext {
  clientGeneratedId: string
}

function storeDestinationEnvironmentTaskLaunch(record: ReturnType<typeof createEnvironmentTaskLaunch>): void {
  let storageFailure = storeEnvironmentTaskLaunch(record)
  if (record.directTurnTelemetrySeed && (storageFailure === 'too-large' || storageFailure === 'storage-full')) {
    const telemetryStorageFailure = storageFailure
    storageFailure = storeEnvironmentTaskLaunch({...record, directTurnTelemetrySeed: undefined})
    if (!storageFailure) {
      // Optional telemetry metadata must not block startup, but dropping it remains diagnosable.
      // eslint-disable-next-line no-console
      console.error('Failed to persist initial environment task launch telemetry seed', telemetryStorageFailure)
    }
  }
  if (storageFailure) {
    throw new Error(`The task was created, but its environment startup could not be saved (${storageFailure}).`)
  }
}

async function resolveDirectTurnTelemetrySeed(
  seed: DirectTurnTelemetrySeed | undefined,
  taskPromise: Promise<HydratedAPITask>,
  identity: {environmentId: string; taskId: string; sessionId: string},
): Promise<DirectTurnTelemetrySeed | undefined> {
  if (!seed) return undefined
  const bareSeed = {
    requestId: seed.requestId,
    timing: seed.timing,
    surface: seed.surface,
  }
  try {
    const task = await taskPromise
    const context = resolveEnvironmentTaskTelemetryContext(seed, identity, task)
    return context ? {...bareSeed, context} : bareSeed
  } catch {
    return bareSeed
  }
}

/**
 * A host that predates the approval-mode config cannot express `assisted`, and rejecting the
 * command must not abort task startup. Degrade to the most restrictive mode the host does
 * support rather than inheriting whatever default it would otherwise run with.
 */
async function applyApprovalModeWithLegacyFallback(
  session: EstablishedAhpSession,
  approvalMode: SessionApprovalMode,
): Promise<void> {
  try {
    await session.commands.setApprovalMode(approvalMode)
  } catch {
    await session.commands.setApproveAll(false)
  }
}

export function useCreateEnvironmentTask(options?: UseCreateEnvironmentTaskOptions) {
  const queryClient = useQueryClient()
  const config = options?.optimisticTaskListConfig
  const hasContributedInstructionsProvider = useHasContributedInstructionsProvider()
  const destinationStartupEnabled = isFeatureEnabled('copilot_mission_control_environment_task_destination_startup')
  const contributedInstructionSnapshot = useOptionalContributedInstructionSnapshot()
  const legacyContributedInstructionSnapshot = useLegacyContributedInstructionSnapshot()
  const currentContributedInstructionSnapshot = hasContributedInstructionsProvider
    ? contributedInstructionSnapshot
    : legacyContributedInstructionSnapshot

  return useMutation<CreateEnvironmentTaskResult, Error, CreateEnvironmentTaskInput, MutationContext>({
    onMutate: async (input): Promise<MutationContext> => {
      const clientGeneratedId = input.clientGeneratedId ?? uuid()
      input.clientGeneratedId = clientGeneratedId
      if (!config) return {clientGeneratedId}

      await queryClient.cancelQueries({queryKey: config.queryKey})
      const optimistic = createOptimisticEnvironmentTask({
        clientGeneratedId,
        problemStatement: input.prompt.text,
      })
      queryClient.setQueryData<TaskListPage>(config.queryKey, oldData =>
        writeTasks(oldData, [optimistic, ...readTasks(oldData)].slice(0, config.pageSize)),
      )
      return {clientGeneratedId}
    },
    mutationFn: async ({
      environmentId,
      prompt,
      customAgent,
      repositoryInstructions,
      repositoryNwo,
      repositoryCloneUrl,
      baseRef,
      managedSandbox,
      chatLaunch,
      agentMode,
      approvalMode,
      mcpPermissionMode,
      configureSession,
      scopeLogin,
      space,
      clientGeneratedId,
      directTurnTelemetrySeed: requestedDirectTurnTelemetrySeed,
    }): Promise<CreateEnvironmentTaskResult> => {
      const directTurnTelemetrySeed = managedSandbox ? requestedDirectTurnTelemetrySeed : undefined
      const normalizedScopeLogin = scopeLogin?.trim()
      if (environmentId === SANDBOX_ENVIRONMENT_ID && !repositoryNwo && !normalizedScopeLogin) {
        throw new Error('Select a personal or organization scope before starting Chat.')
      }
      const reasoningEffort = prompt.model?.config?.reasoningEffort
      const persistedPromptSelection = {
        ...(prompt.model?.id ? {model: prompt.model.id} : {}),
        ...(isReasoningEffort(reasoningEffort) ? {reasoningEffort} : {}),
      }
      const environmentTaskInput: CreateEnvironmentBoundTaskInput = repositoryNwo
        ? {
            environmentId,
            problemStatement: prompt.text,
            ...persistedPromptSelection,
            repositoryNwo,
            ...(baseRef ? {baseRef} : {}),
            ...(space ? {space} : {}),
          }
        : {
            environmentId,
            problemStatement: prompt.text,
            ...persistedPromptSelection,
            ...(space ? {space} : {}),
            ...(normalizedScopeLogin ? {computeScope: normalizedScopeLogin} : {}),
          }
      const {taskId, sessionId, boundEnvironmentId} = await createEnvironmentBoundTask(environmentTaskInput)
      const taskPromise = fetchTask({taskId})
      void (async () => {
        try {
          await taskPromise
        } catch {
          // Startup can fail before the normal await; retain the rejecting promise without leaving it unhandled.
        }
      })()

      if (config && clientGeneratedId) {
        patchOptimisticTaskId(queryClient, config, clientGeneratedId, taskId)
      }

      const relayEnvironmentId = boundEnvironmentId ?? environmentId
      const identity = {taskId, environmentId: relayEnvironmentId, sessionId}
      const instructionRulePublisher = new InstructionRuleSessionPublisher()
      const repositoryRules = deriveInstructionRules({
        personalInstructions: null,
        customInstructions: repositoryInstructions ?? [],
      })
      instructionRulePublisher.commitRepositoryInstructions(repositoryRules, taskId)
      instructionRulePublisher.activate(taskId)
      const contributedInstructions = mergeContributedInstructionsMetadata(
        prompt.contributedInstructions ?? [],
        toContributedInstructionsMetadata(currentContributedInstructionSnapshot),
        toContributedInstructionsMetadata(
          normalizeContributedInstructions({
            personalInstructions: null,
            customInstructions: repositoryInstructions ?? [],
          }),
        ),
      )
      const submittedPrompt = contributedInstructions.length > 0 ? {...prompt, contributedInstructions} : prompt
      const startupMode = resolveEnvironmentTaskStartupMode(agentMode, mcpPermissionMode)
      const sessionScope = resolveSessionScope(repositoryNwo, normalizedScopeLogin)
      const destinationOwnsStartup = destinationStartupEnabled && !configureSession && !approvalMode
      const createLaunchRecord = () =>
        createEnvironmentTaskLaunch({
          taskId,
          sessionId,
          environmentId: relayEnvironmentId,
          requestId: directTurnTelemetrySeed?.requestId ?? uuid(),
          prompt: submittedPrompt,
          managedSandbox: managedSandbox ?? false,
          repositoryNwo,
          repositoryCloneUrl,
          baseRef,
          ...(normalizedScopeLogin ? {scopeLogin: normalizedScopeLogin} : {}),
          startupMode,
          customAgent,
          directTurnTelemetrySeed: directTurnTelemetrySeed
            ? {
                requestId: directTurnTelemetrySeed.requestId,
                timing: directTurnTelemetrySeed.timing,
                surface: directTurnTelemetrySeed.surface,
              }
            : undefined,
          ...(chatLaunch && environmentId === SANDBOX_ENVIRONMENT_ID ? {baselineHostTools: true} : {}),
        })
      let launchRecord: ReturnType<typeof createEnvironmentTaskLaunch> | undefined
      const resolvedDirectTurnTelemetrySeedPromise = resolveDirectTurnTelemetrySeed(
        directTurnTelemetrySeed,
        taskPromise,
        identity,
      )
      const startupLaunch = managedSandbox
        ? {
            kind: 'managed-sandbox' as const,
            environmentId: relayEnvironmentId,
            ownerLogin: repositoryNwo?.split('/')[0] || space?.owner || normalizedScopeLogin,
          }
        : undefined

      if (destinationOwnsStartup) {
        try {
          launchRecord = createLaunchRecord()
          storeDestinationEnvironmentTaskLaunch(launchRecord)
          beginTaskStartup(taskId, submittedPrompt, startupLaunch)
          instructionRulePublisher.deactivate(taskId)
        } catch (error) {
          instructionRulePublisher.deactivate(taskId, true)
          throw error
        } finally {
          instructionRulePublisher.dispose()
        }
      } else {
        if (directTurnTelemetrySeed) {
          try {
            launchRecord = createLaunchRecord()
          } catch (error) {
            // Optional telemetry retention must not abort an otherwise valid inline task launch.
            // eslint-disable-next-line no-console
            console.error('Failed to retain environment task launch telemetry owner', error)
          }
        }
        beginTaskStartup(taskId, submittedPrompt, startupLaunch)

        const directTurnTelemetry = directTurnTelemetrySeed ? createCopilotChatTurnLatencyTracker() : undefined
        let resolvedInlineSeed: DirectTurnTelemetrySeed | undefined
        let inlineOwnerRegistered = false
        let resolveInlineOwnerTask: ReturnType<typeof captureEnvironmentTaskLaunchTaskResolver>
        if (directTurnTelemetrySeed && directTurnTelemetry) {
          void (async () => {
            const seed = await resolvedDirectTurnTelemetrySeedPromise
            resolvedInlineSeed = seed
            if (inlineOwnerRegistered) {
              if (seed?.context) resolveInlineOwnerTask?.(await taskPromise)
            } else if (seed?.context || !launchRecord) {
              directTurnTelemetry.submissionContextResolved?.(directTurnTelemetrySeed.requestId, seed?.context ?? null)
            }
          })()
        }
        const bootstrap = createMissionControlEstablishedAhpBootstrap({
          provisionSession: async () => {
            throw new Error('Environment task bootstrap cannot provision a second task.')
          },
          createRelayOptions: (relayIdentity, request) => ({
            fetcher: mcFetcher,
            createSink: () => new AhpChatStore(),
            acceptInstructionRules: true,
            hostTools:
              chatLaunch && environmentId === SANDBOX_ENVIRONMENT_ID ? {kind: 'baseline-at-create'} : {kind: 'none'},
            ...createAhpRelayDiagnostics(relayIdentity.taskId ?? relayIdentity.sessionId),
            // `recover` is this path's first-establish mode (the coordinator always has an
            // identity), so this is where the relay actually creates the daemon session.
            // `attach`/`wake` deliberately omit `createSession` so reconnects only subscribe.
            ...(request?.mode === 'recover'
              ? {
                  createSession: {
                    // The host applies the initial mode before registering the session;
                    // a later `session/configChanged` can be dropped while it comes up.
                    ...(startupMode ? {mode: startupMode} : {}),
                    // Grants the SDK `session-store` capability that backs `/chronicle`.
                    // copilot-host reads it only at session creation and it cannot be
                    // added afterwards, so it has to ride along here.
                    ...(isCloudSessionStoreEnabled() ? {enableSessionStore: true} : {}),
                    // Scope facts (owner, and the repository when there is one)
                    // the agent cannot otherwise discover; appended to the
                    // host's `environment_context` prompt section.
                    ...(sessionScope ? {sessionScope} : {}),
                    // A sandbox runs the managed Playwright MCP server, which
                    // only reaches loopback.
                    ...(managedSandbox ? {managedPlaywrightMcp: true} : {}),
                    ...(repositoryNwo
                      ? {
                          project: {
                            nwo: repositoryNwo,
                            cloneUrl: repoCloneUrl(repositoryNwo, repositoryCloneUrl),
                            ...(baseRef ? {baseBranch: baseRef} : {}),
                          },
                        }
                      : {}),
                  },
                }
              : {}),
          }),
          directTurnTelemetry,
        })
        const initialSubmission = createEstablishedAhpInitialSubmissionCoordinator({
          bootstrap,
          identity,
        })
        // Names the launch submission so a startup that fails after the dispatch can
        // retire exactly the one it armed.
        let launchSubmissionId: number | undefined
        const configureFirstTurn =
          configureSession ??
          (startupMode || approvalMode
            ? async (session: EstablishedAhpSession) => {
                if (approvalMode) {
                  await Promise.all([
                    ensureSessionMode(session, startupMode),
                    applyApprovalModeWithLegacyFallback(session, approvalMode),
                  ])
                } else {
                  await applyEnvironmentTaskStartupMode(session, startupMode)
                }
              }
            : undefined)
        if (directTurnTelemetrySeed && launchRecord) expectInlineEnvironmentTaskLaunchOwner(launchRecord)
        void (async () => {
          let ownerTransferred = false
          let firstTurnId: string | undefined
          try {
            const establishedSession = await initialSubmission.submit({
              prompt: submittedPrompt,
              origin: 'new',
              ...(directTurnTelemetrySeed ? {requestId: directTurnTelemetrySeed.requestId} : {}),
              ...(directTurnTelemetrySeed
                ? {
                    directTurnTelemetrySurface: directTurnTelemetrySeed.surface,
                    directTurnTelemetryContextPending: true,
                    directTurnTelemetryTiming: directTurnTelemetrySeed.timing,
                    onTurnDispatched: (turnId: string) => {
                      firstTurnId = turnId
                    },
                  }
                : {}),
              ...(customAgent
                ? {
                    preparePrompt: createCustomAgentPromptPreparer(customAgent),
                  }
                : {}),
              ...(configureFirstTurn ? {configureSession: configureFirstTurn} : {}),
              beforeDispatch: async () => {
                launchSubmissionId = markUserStartedTurn(taskId)
              },
              afterDispatch: receipt => {
                if (launchSubmissionId !== undefined) bindUserStartedTurn(taskId, launchSubmissionId, receipt)
              },
            })
            if (establishedSession.view.getSnapshot().passive) {
              throw new Error('This environment is read-only.')
            }
            completeTaskStartup(taskId)
            if (directTurnTelemetrySeed && launchRecord) {
              registerInlineEnvironmentTaskLaunchOwner(
                {...launchRecord, directTurnTelemetrySeed: resolvedInlineSeed ?? launchRecord.directTurnTelemetrySeed},
                establishedSession,
                bootstrap,
                firstTurnId,
                directTurnTelemetry,
              )
              inlineOwnerRegistered = true
              resolveInlineOwnerTask = captureEnvironmentTaskLaunchTaskResolver(taskId)
              ownerTransferred = true
            }
          } catch (error) {
            if (launchSubmissionId !== undefined) unmarkUserStartedTurn(taskId, launchSubmissionId)
            if (directTurnTelemetrySeed) abandonInlineEnvironmentTaskLaunchOwner(taskId)
            failTaskStartup(taskId, error)
            reportRelayError(taskId, error)
          } finally {
            const currentRelayEntry = getRelayEntry(taskId)
            const relayEntry =
              currentRelayEntry?.environmentId === identity.environmentId &&
              currentRelayEntry.sessionId === identity.sessionId &&
              currentRelayEntry.channel === undefined
                ? currentRelayEntry
                : undefined
            initialSubmission.dispose()
            instructionRulePublisher.deactivate(taskId)
            instructionRulePublisher.dispose()
            if (!ownerTransferred && directTurnTelemetrySeed) {
              directTurnTelemetry?.submissionContextResolved?.(directTurnTelemetrySeed.requestId, null)
            }
            if (!ownerTransferred) bootstrap.dispose()
            discardRelayInstructionRulesWhenUnused(taskId, relayEntry, () => discardInstructionRuleSession(taskId))
          }
        })()
      }

      const task: HydratedAPITask = await taskPromise
      if (destinationOwnsStartup && directTurnTelemetrySeed && launchRecord) {
        const resolvedDirectTurnTelemetrySeed = await resolvedDirectTurnTelemetrySeedPromise
        if (resolvedDirectTurnTelemetrySeed) {
          replaceEnvironmentTaskLaunch(
            taskId,
            current => ({...current, directTurnTelemetrySeed: resolvedDirectTurnTelemetrySeed}),
            launchRecord,
          )
        }
      }
      return {
        task,
        sessionId,
        environmentId: relayEnvironmentId,
      }
    },
    onSuccess: ({task}, _input, context) => {
      if (!config || !context) return
      const transformed = transformAPITaskToUnhydrated(task)
      queryClient.setQueryData<TaskListPage>(config.queryKey, oldData => {
        const cached = readTasks(oldData)
        const withoutDupes = cached.filter(
          t =>
            t.clientGeneratedId !== context.clientGeneratedId &&
            (!transformed || (t.id !== transformed.id && t.task_id !== transformed.id)),
        )
        const next = transformed ? sortTasksByDate([transformed, ...withoutDupes]) : withoutDupes
        return writeTasks(oldData, next.slice(0, config.pageSize))
      })
      queryClient.invalidateQueries({queryKey: config.queryKey, exact: true})

      for (const key of config.additionalInvalidateKeys ?? []) {
        queryClient.invalidateQueries({queryKey: key})
      }
    },
    onError: (_error, _input, context) => {
      if (!config || !context) return
      queryClient.setQueryData<TaskListPage>(config.queryKey, oldData =>
        writeTasks(
          oldData,
          readTasks(oldData).filter(t => t.clientGeneratedId !== context.clientGeneratedId),
        ),
      )
    },
  })
}

function patchOptimisticTaskId(
  queryClient: ReturnType<typeof useQueryClient>,
  config: OptimisticTaskListConfig,
  clientGeneratedId: string,
  taskId: string,
): void {
  queryClient.setQueryData<TaskListPage>(config.queryKey, oldData => {
    const cached = readTasks(oldData)
    if (!cached.some(task => task.clientGeneratedId === clientGeneratedId)) return oldData
    return writeTasks(
      oldData,
      cached.map(task =>
        task.clientGeneratedId === clientGeneratedId ? {...task, id: taskId, task_id: taskId} : task,
      ),
    )
  })
}

interface CreateOptimisticEnvironmentTaskParams {
  clientGeneratedId: string
  problemStatement: string
}

export function createOptimisticEnvironmentTask({
  clientGeneratedId,
  problemStatement,
}: CreateOptimisticEnvironmentTaskParams): OptimisticTask {
  return {
    resource_type: 'pull',
    resource_id: -1,
    resource_global_id: '',
    created_at: new Date().toISOString(),
    session_count: 1,
    latest_session_updated_at: new Date().toISOString(),
    latest_session_state: 'in_progress',
    isOptimistic: true,
    clientGeneratedId,
    problemStatement,
    name: problemStatement,
  }
}
